/**
 * Substitution threshold meter (Layer 2 — F6.6).
 *
 * Shows a batch's cumulative substitution usage against the TGP limits from
 * the active config profile: the substituted-days fraction and the longest
 * consecutive-substitution run, each vs its limit. The `band` escalates
 * ok → warning_50 → warning_75 → warning_90 → over; `over_threshold` is the
 * hard gate. Decimal strings are parsed with decimal.js (never Number()).
 */

import { Decimal } from "@compliance/domain";
import { Alert, Card, Group, Progress, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconGauge } from "@tabler/icons-react";
import { useSubstitutionMeter } from "../api/hooks";
import type { SubstitutionBand } from "../api/types";

const BAND_COLOR: Record<SubstitutionBand, string> = {
  ok: "teal",
  warning_50: "yellow",
  warning_75: "orange",
  warning_90: "orange",
  over: "red",
};

const BAND_LABEL: Record<SubstitutionBand, string> = {
  ok: "Within limit",
  warning_50: "50% of limit",
  warning_75: "75% of limit",
  warning_90: "90% of limit",
  over: "Over threshold",
};

// `band` is an open string on the wire — map known values, fall back for any
// new band the BE introduces rather than crashing or hiding the meter.
function bandColor(band: string): string {
  return BAND_COLOR[band as SubstitutionBand] ?? "gray";
}

function bandLabel(band: string): string {
  return BAND_LABEL[band as SubstitutionBand] ?? band;
}

/** Decimal-string → percentage number, clamped to [0, 100] for the bar. */
function toPct(decimalString: string): number {
  try {
    const n = new Decimal(decimalString).times(100).toNumber();
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
  } catch {
    return 0;
  }
}

/** Decimal-string fraction → display percent, e.g. "0.33" → "33%". */
function fractionLabel(decimalString: string): string {
  try {
    return `${new Decimal(decimalString).times(100).toDecimalPlaces(1).toString()}%`;
  } catch {
    return decimalString;
  }
}

export function SubstitutionMeterCard({ batchId }: { batchId: string }) {
  const meterQ = useSubstitutionMeter(batchId);

  if (meterQ.isPending || meterQ.isError) {
    // Non-blocking: the meter is supplementary to the corrections table, so a
    // failure stays quiet rather than taking over the tab.
    return null;
  }

  const m = meterQ.data;
  const color = bandColor(m.band);
  const barValue = toPct(m.utilization);

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <IconGauge size={18} />
            <Text fw={600} size="sm">
              Substitution threshold (TGP)
            </Text>
          </Group>
          <Text size="sm" fw={600} c={color}>
            {bandLabel(m.band)}
          </Text>
        </Group>

        <Progress value={barValue} color={color} size="lg" radius="sm" striped={m.over_threshold} />

        <Group justify="space-between" wrap="wrap" gap="lg">
          <Stat
            label="Days substituted"
            value={`${m.substituted_days} / ${m.total_days}`}
            sub={`${fractionLabel(m.fraction)} of ${fractionLabel(m.limit_fraction)} limit`}
          />
          <Stat
            label="Longest run"
            value={`${m.max_consecutive_days} / ${m.limit_consecutive_days}`}
            sub="consecutive days"
          />
          <Stat label="Utilization" value={fractionLabel(m.utilization)} sub="of the limit" />
        </Group>

        {m.over_threshold ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              This batch is over the substitution threshold. Per policy, excess substitutions must
              be re-proposed as exclusions — there is no re-enable affordance.
            </Text>
          </Alert>
        ) : null}
      </Stack>
    </Card>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        {label}
      </Text>
      <Text size="lg" fw={700} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {sub}
      </Text>
    </Stack>
  );
}
